import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Heading — renders h1–h6 from a numeric `level` prop.
 * The optional `as` prop overrides the rendered element for cases where the visual
 * heading level must differ from the document outline (e.g. an h2 styled like an h1).
 *
 * @atomic-tier Atom
 * @category Typography
 */

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6
export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** The semantic heading level — determines the rendered element. */
  level: HeadingLevel
  /** Override the rendered element while preserving the intended level in props. */
  as?: HeadingTag
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level, as, className, children, ...props }, ref) => {
    const Component: HeadingTag = as ?? (`h${level}` as HeadingTag)
    return (
      <Component ref={ref} className={cn(className)} {...props}>
        {children}
      </Component>
    )
  },
)
Heading.displayName = "Heading"

export { Heading }
